import { useEffect, useMemo } from "react";

import { Checkbox, Group, TextInput, UnstyledButton } from "@mantine/core";
import { UseFormReturnType } from "@mantine/form";

import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { IconGripVertical, IconTrash } from "@tabler/icons-react";
import { omit } from "lodash";

import { ChecklistTemplateTaskType } from "../../../../types/checklist-template";
import { ChecklistTemplateFormType } from "../zod-schema/checklist-template-zod";

function ChecklistTaskDragAndDrop({
  tasks,
  form,
  refs,
}: {
  tasks: ChecklistTemplateTaskType[];
  form: UseFormReturnType<ChecklistTemplateFormType>;
  refs: Record<string, React.RefObject<HTMLInputElement>>;
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { over, active } = event;

    if (!over) return;

    const activeIndex = active.data?.current?.sortable?.index;
    const overIndex = over.data?.current?.sortable?.index;

    if (activeIndex === undefined || overIndex === undefined || activeIndex === overIndex) {
      return;
    }

    const tasksEntries = Object.entries(form.values.tasks);

    const [movedTask] = tasksEntries.splice(activeIndex, 1);

    tasksEntries.splice(overIndex, 0, movedTask);

    const reorderedTasks = Object.fromEntries(tasksEntries);

    form.setFieldValue("tasks", reorderedTasks);
  };

  return (
    <DndContext sensors={sensors} onDragEnd={handleDragEnd} collisionDetection={closestCenter}>
      <SortableContext items={tasks.map((task) => task.id)} strategy={verticalListSortingStrategy}>
        {tasks.map((task, index) => (
          <SortableItem task={task} form={form} refs={refs} key={task.id} index={index} />
        ))}
      </SortableContext>
    </DndContext>
  );
}

function SortableItem({
  task,
  form,
  index,
  refs,
}: {
  task: ChecklistTemplateTaskType;
  form: UseFormReturnType<ChecklistTemplateFormType>;
  index: number;
  refs: Record<string, React.RefObject<HTMLInputElement>>;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: task.id,
  });

  const inputRef = useMemo(() => refs[task.id], [refs, task.id]);

  useEffect(() => {
    if (inputRef?.current) {
      inputRef.current.focus();
    }
  }, [inputRef]);

  useEffect(() => {
    const escListener = (event: KeyboardEvent) => {
      if (
        event.key === "Escape" &&
        inputRef?.current &&
        inputRef.current.value === "" &&
        document.activeElement === inputRef.current
      ) {
        form.setValues((prev) => omit(prev, `tasks.${task.id}`));
      }
    };

    addEventListener("keydown", escListener);

    return () => {
      removeEventListener("keydown", escListener);
    };
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    backgroundColor: isDragging ? "var(--mantine-color-gray-1)" : "transparent",
  };

  return (
    <Group
      style={style}
      ref={setNodeRef}
      {...attributes}
      h={42}
      gap={0}
      wrap="nowrap"
      data-testid={`checklist-task-${index}`}
    >
      <IconGripVertical
        size={18}
        stroke={1.5}
        color="var(--mantine-color-gray-6)"
        {...listeners}
        aria-label="Drag and drop task"
        cursor={isDragging ? "grabbing" : "grab"}
      />

      <Group gap={0} w="100%" wrap="nowrap">
        <Checkbox
          disabled
          size="md"
          styles={{ input: { backgroundColor: "transparent", cursor: "default" } }}
        />
        <TextInput
          required
          w="100%"
          size="xs"
          ref={inputRef}
          data-testid={`checklist-task-input-${index}`}
          styles={{ input: { border: "none", backgroundColor: "transparent" } }}
          {...form.getInputProps(`tasks.${task.id}.name`, { type: "input" })}
        />
        <UnstyledButton
          data-testid={`checklist-task-delete-${index}`}
          onClick={() => form.setValues((prev) => omit(prev, `tasks.${task.id}`))}
        >
          <IconTrash size={24} stroke={1.5} color="var(--mantine-color-red-7)" />
        </UnstyledButton>
      </Group>
    </Group>
  );
}

export default ChecklistTaskDragAndDrop;
